import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { FULL_MONTHS, thisMonth, thisYear, classNames } from "../lib/utils";
import { Users, BarChart3, ClipboardCheck, FileDown, Mail, TrendingUp } from "lucide-react";
import RosterReport from "./reports/RosterReport";
import PerformanceReport from "./reports/PerformanceReport";
import QAReport from "./reports/QAReport";
import QAYoYReport from "./reports/QAYoYReport";

const TABS = [
  { id: "roster", label: "Roster Report", icon: Users },
  { id: "performance", label: "KPI Performance", icon: BarChart3 },
  { id: "qa", label: "QA Audits", icon: ClipboardCheck },
  { id: "yoy", label: "QA YoY Trend", icon: TrendingUp },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const hmHomes = (user.assigned_homes && user.assigned_homes.length
    ? user.assigned_homes
    : (user.assigned_home ? [user.assigned_home] : []));
  const isMultiHomeHM = user.access_role === "House Manager" && hmHomes.length > 1;
  const [tab, setTab] = useState("roster");
  const [homes, setHomes] = useState([]);
  const [home, setHome] = useState(
    user.access_role === "House Manager" ? (isMultiHomeHM ? "" : (hmHomes[0] || "")) : ""
  );
  const [classification, setClassification] = useState("");
  const [gender, setGender] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [year, setYear] = useState(thisYear());
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/homes").then(({ data }) => setHomes(data)); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  const canSendDigest = ["CEO", "Project Manager", "Administration"].includes(user.access_role);

  const sendDigest = async () => {
    const choice = window.prompt(
      "Send the monthly digest to:\n" +
      "1) The configured CEO + Project Manager recipients\n" +
      "2) Only my email (test)\n" +
      "Type 1 or 2:",
      "2"
    );
    if (!choice) return;
    const body = {};
    if (month && year) { body.month = month; body.year = year; }
    if (choice.trim() === "2") body.test_recipient = user.email;
    setSending(true);
    try {
      const { data } = await api.post("/reports/email-digest", body);
      toast.success(`Digest sent to ${data.recipients.join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  // Auth-aware PDF download (browser <a download> won't include the Authorization header,
  // so we fetch the bytes via the api client and trigger a blob download).
  const downloadOpsPDF = async () => {
    try {
      const params = { month, year };
      if (home) params.home = home;
      // On the QA Audits tab the branded PDF's QA section must match the
      // on-screen table, so pass qa_month. On other tabs keep the historic
      // full-year QA view (backwards compatible).
      if (tab === "qa") params.qa_month = month;
      const res = await api.get("/reports/operations.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NestVillage-Operations-${year}-${String(month).padStart(2,"0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF generation failed");
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">Reports</p>
          <h1 className="text-h1 mt-1">Reports & Exports</h1>
          <p className="text-sm text-slate-500 mt-1">Pull headcount, performance, and audit data with filters. One-click CSV or branded PDF.</p>
        </div>
        <button
          onClick={downloadOpsPDF}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-fuchsia-700 hover:bg-fuchsia-800 text-white text-sm font-semibold shadow-sm"
          data-testid="download-ops-pdf-btn"
          title="Download a branded server-rendered PDF with letterhead"
        >
          <FileDown size={15} /> Download Branded PDF
        </button>
        <Link
          to={`/reports/print?${new URLSearchParams({
            home: home || "ALL", month, year,
            // Classification/Gender only apply to staff-scored views. On the QA
            // Audits tab they have no meaning; drop them from the print URL so
            // the printable's QA section can be period-scoped to (month, year).
            ...(tab === "qa" ? {} : { classification, gender: "" }),
            tab,
          }).toString()}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm"
          data-testid="generate-printable-report-btn"
        >
          <FileDown size={15} /> Generate Printable (Browser)
        </Link>
        {canSendDigest && (
          <button
            onClick={sendDigest}
            disabled={sending}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold shadow-sm disabled:opacity-50"
            data-testid="send-email-digest-btn"
            title="Email the monthly digest to CEO + Project Manager (or to yourself for testing)"
          >
            <Mail size={15} /> {sending ? "Sending…" : "Send Email Digest"}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200" data-testid="reports-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`reports-tab-${t.id}`}
            className={classNames(
              "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition",
              tab === t.id ? "border-sky-700 text-sky-800" : "border-transparent text-slate-500 hover:text-slate-700"
            )}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="nv-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="reports-filters">
          {(user.access_role !== "House Manager" || isMultiHomeHM) && (
            <div>
              <label className="text-small-caps">Home</label>
              <select value={home} onChange={(e) => setHome(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="report-home">
                <option value="">All Homes</option>
                {homes.map((h) => <option key={h.home_name} value={h.home_name}>{h.home_name}</option>)}
              </select>
            </div>
          )}
          {/* Classification and Gender apply to staff-scored reports only.
              QA Audits measures whole-home compliance and has no per-staff
              classification/gender in the saved data, so both controls are
              omitted on the QA tab. They remain available for Roster Report,
              KPI Performance, and QA YoY Trend where they filter real data. */}
          {tab !== "qa" && (
            <div>
              <label className="text-small-caps">Classification</label>
              <select value={classification} onChange={(e) => setClassification(e.target.value)} disabled={tab === "yoy"} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 disabled:bg-slate-50 disabled:text-slate-400" data-testid="report-classification">
                <option value="">All</option>
                <option>Direct Care Worker</option>
                <option>Contract Care Worker</option>
                <option>House Manager</option>
                <option>Administration</option>
              </select>
            </div>
          )}
          {tab !== "qa" && (
            <div>
              <label className="text-small-caps">Gender</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)} disabled={tab === "yoy"} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 disabled:bg-slate-50 disabled:text-slate-400" data-testid="report-gender">
                <option value="">All</option><option>Male</option><option>Female</option>
              </select>
            </div>
          )}
          {tab !== "roster" && tab !== "yoy" && (
            <>
              <div>
                <label className="text-small-caps">Month</label>
                <select value={month} onChange={(e) => setMonth(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="report-month">
                  {FULL_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-small-caps">Year</label>
                <input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="report-year" />
              </div>
            </>
          )}
          {tab === "yoy" && (
            <div>
              <label className="text-small-caps">Year</label>
              <input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="report-year" />
            </div>
          )}
        </div>
      </div>

      {tab === "roster" && <RosterReport home={home} classification={classification} gender={gender} />}
      {tab === "performance" && <PerformanceReport home={home} classification={classification} gender={gender} month={month} year={year} />}
      {tab === "qa" && <QAReport home={home} month={month} year={year} />}
      {tab === "yoy" && <QAYoYReport home={home} year={year} />}
    </div>
  );
}
